import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, {
  FullscreenControl, GeolocateControl, Layer, NavigationControl, Popup, ScaleControl, Source,
} from 'react-map-gl/maplibre';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { api, apiRoutes } from '../../api';
import Header from '../../shared/layout/Header';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import './GISPlatform.css';

// Mapbox Draw still ships Mapbox GL class names. Point it at MapLibre's
// canvas/control classes so pointer events and cursors work reliably.
if (MapboxDraw.constants?.classes) {
  MapboxDraw.constants.classes.CANVAS = 'maplibregl-canvas';
  MapboxDraw.constants.classes.CONTROL_BASE = 'maplibregl-ctrl';
  MapboxDraw.constants.classes.CONTROL_PREFIX = 'maplibregl-ctrl-';
  MapboxDraw.constants.classes.CONTROL_GROUP = 'maplibregl-ctrl-group';
  MapboxDraw.constants.classes.ATTRIBUTION = 'maplibregl-ctrl-attrib';
}

const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };
const DEFAULT_FILTER = { field: '', operator: 'eq', value: '' };
const OPERATORS = [
  ['eq', 'is / ='], ['neq', 'is not / ≠'], ['gt', '>'], ['gte', '≥'], ['lt', '<'], ['lte', '≤'],
  ['contains', 'contains'], ['startswith', 'starts with'], ['endswith', 'ends with'],
  ['isnull', 'is empty'], ['in', 'is one of'],
];
const BASEMAPS = {
  streets: { label: 'Streets', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], attribution: '© OpenStreetMap' },
  light: { label: 'Light', tiles: ['https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'], attribution: '© OpenStreetMap © CARTO' },
  satellite: { label: 'Satellite', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], attribution: 'Tiles © Esri' },
};
const PALETTES = {
  emerald: ['#d1fae5', '#6ee7b7', '#10b981', '#047857', '#064e3b'],
  sunset: ['#fff7ed', '#fdba74', '#f97316', '#dc2626', '#7f1d1d'],
  ocean: ['#e0f2fe', '#7dd3fc', '#0ea5e9', '#0369a1', '#0c4a6e'],
  spectrum: ['#440154', '#3b528b', '#21918c', '#5ec962', '#fde725'],
};

function fieldValue(value) {
  return value === null || value === undefined ? '' : String(value);
}

function isNumeric(field) {
  return ['IntegerField', 'AutoField', 'BigIntegerField', 'FloatField', 'DecimalField'].includes(field?.type);
}

function operatorsForField(field) {
  if (!field) return OPERATORS;
  if (field.type === 'BooleanField') return OPERATORS.filter(([value]) => ['eq', 'neq', 'isnull'].includes(value));
  if (isNumeric(field)) return OPERATORS.filter(([value]) => ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'isnull', 'in'].includes(value));
  return OPERATORS;
}

function geometryCenter(geometry) {
  const points = [];
  const visit = (coordinates) => {
    if (typeof coordinates?.[0] === 'number') points.push(coordinates);
    else coordinates?.forEach(visit);
  };
  visit(geometry?.coordinates);
  if (!points.length) return null;
  const lngs = points.map((point) => point[0]);
  const lats = points.map((point) => point[1]);
  return {
    longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
  };
}

function pointInRing(point, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]; const [xj, yj] = ring[j];
    const intersects = ((yi > point[1]) !== (yj > point[1]))
      && point[0] < ((xj - xi) * (point[1] - yi)) / ((yj - yi) || Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function makeMapStyle(basemap) {
  const source = BASEMAPS[basemap] || BASEMAPS.streets;
  return {
    version: 8,
    sources: { basemap: { type: 'raster', tiles: source.tiles, tileSize: 256, attribution: source.attribution } },
    layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
  };
}

function buildSymbology(features, field, method, classCount, manualBreaks, paletteName) {
  const colors = PALETTES[paletteName] || PALETTES.emerald;
  if (!field) return { color: '#23805a', legend: [{ label: 'All features', color: '#23805a' }] };
  const rawValues = features.map((feature) => feature.properties?.[field]).filter((value) => value !== null && value !== undefined && value !== '');
  const numericValues = rawValues.map(Number).filter(Number.isFinite);
  if (numericValues.length === rawValues.length && numericValues.length) {
    const count = Math.max(2, Math.min(Number(classCount) || 5, colors.length));
    const min = Math.min(...numericValues); const max = Math.max(...numericValues);
    let breaks = method === 'manual'
      ? manualBreaks.split(',').map(Number).filter(Number.isFinite).sort((a, b) => a - b)
      : Array.from({ length: count - 1 }, (_, index) => min + ((max - min) * (index + 1)) / count);
    breaks = breaks.slice(0, colors.length - 1);
    const expression = ['step', ['to-number', ['get', field], min], colors[0]];
    breaks.forEach((value, index) => expression.push(value, colors[index + 1]));
    const legend = colors.slice(0, breaks.length + 1).map((color, index) => ({
      color,
      label: index === 0 ? `≤ ${breaks[0]?.toFixed(2) ?? max}`
        : index === breaks.length ? `> ${breaks[index - 1].toFixed(2)}`
          : `${breaks[index - 1].toFixed(2)} – ${breaks[index].toFixed(2)}`,
    }));
    return { color: expression, legend };
  }
  const categories = [...new Set(rawValues.map(String))].slice(0, colors.length);
  const expression = ['match', ['to-string', ['get', field]]];
  categories.forEach((value, index) => expression.push(value, colors[index % colors.length]));
  expression.push('#94a3b8');
  return {
    color: expression,
    legend: categories.map((label, index) => ({ label, color: colors[index % colors.length] })),
  };
}

function FieldInput({ field, value, onChange }) {
  if (field?.type === 'BooleanField') {
    return <select value={fieldValue(value)} onChange={(event) => onChange(event.target.value)}><option value="">—</option><option value="true">True</option><option value="false">False</option></select>;
  }
  return <input type={isNumeric(field) ? 'number' : 'text'} value={fieldValue(value)} onChange={(event) => onChange(event.target.value)} />;
}

function runDrawAction(drawRef, action, fallback = null) {
  const draw = drawRef.current;
  if (!draw) return fallback;
  try {
    return action(draw);
  } catch (_error) {
    // MapLibre can remove the control before React closes a popup. Once the
    // Draw store is gone, discard the stale reference instead of crashing.
    drawRef.current = null;
    return fallback;
  }
}

function clearDraw(drawRef) {
  runDrawAction(drawRef, (draw) => draw.deleteAll());
}

export default function GISPlatform() {
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const drawPurposeRef = useRef('create');
  const ignoreNextMapClickRef = useRef(false);
  const featuresRef = useRef(EMPTY_COLLECTION);
  const [access, setAccess] = useState('loading');
  const [currentUser, setCurrentUser] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [features, setFeatures] = useState(EMPTY_COLLECTION);
  const [selectedIds, setSelectedIds] = useState([]);
  const [draftGeometry, setDraftGeometry] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState('layers');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState([]);
  const [importMappings, setImportMappings] = useState({});
  const [changes, setChanges] = useState([]);
  const [agentSubmissions, setAgentSubmissions] = useState([]);
  const [filters, setFilters] = useState([{ ...DEFAULT_FILTER }]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [distinctValues, setDistinctValues] = useState({});
  const [basemap, setBasemap] = useState('streets');
  const [symbologyField, setSymbologyField] = useState('');
  const [classification, setClassification] = useState('equal');
  const [classCount, setClassCount] = useState(5);
  const [manualBreaks, setManualBreaks] = useState('');
  const [palette, setPalette] = useState('emerald');
  const [batchField, setBatchField] = useState('');
  const [batchValue, setBatchValue] = useState('');

  const dataset = useMemo(() => datasets.find((item) => item.slug === selectedSlug), [datasets, selectedSlug]);
  const selectedFeatures = useMemo(() => features.features.filter((feature) => selectedIds.includes(String(feature.id))), [features, selectedIds]);
  const selectedFeature = selectedFeatures.length === 1 ? selectedFeatures[0] : null;
  const popupPosition = selectedFeature ? geometryCenter(selectedFeature.geometry) : null;
  const selectedCollection = useMemo(() => ({ type: 'FeatureCollection', features: selectedFeatures }), [selectedFeatures]);
  const symbology = useMemo(() => buildSymbology(features.features, symbologyField, classification, classCount, manualBreaks, palette), [features, symbologyField, classification, classCount, manualBreaks, palette]);
  const mapStyle = useMemo(() => makeMapStyle(basemap), [basemap]);
  const agentSubmissionCollection = useMemo(() => ({
    type: 'FeatureCollection',
    features: agentSubmissions.filter((item) => item.geometry).map((item) => ({
      type: 'Feature', id: item.id, geometry: item.geometry,
      properties: { id: item.id, status: item.status, category: item.category, title: item.title },
    })),
  }), [agentSubmissions]);

  useEffect(() => { featuresRef.current = features; }, [features]);
  useEffect(() => {
    if (selectedFeature) {
      setForm(selectedFeature.properties || {});
      setDraftGeometry(selectedFeature.geometry);
    }
  }, [selectedFeature?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([api.get(apiRoutes.auth.user), api.get(apiRoutes.gisPlatform.datasets)])
      .then(([userResponse, datasetResponse]) => {
        if (!userResponse.data.is_gis_team) throw new Error('forbidden');
        const mapDatasets = datasetResponse.data.filter((item) => item.map_enabled);
        setCurrentUser(userResponse.data);
        setDatasets(datasetResponse.data);
        setSelectedSlug(mapDatasets[0]?.slug || '');
        setAccess('allowed');
      })
      .catch((error) => setAccess(error.response?.status === 401 ? 'login' : 'denied'));
  }, []);

  const loadFeatures = useCallback(async () => {
    if (!selectedSlug || !mapRef.current) return;
    const bounds = mapRef.current.getMap().getBounds();
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(',');
    try {
      const response = await api.get(apiRoutes.gisPlatform.features(selectedSlug), {
        params: { bbox, limit: 500, filters: appliedFilters.length ? JSON.stringify(appliedFilters) : undefined },
      });
      setFeatures(response.data);
      setSelectedIds((current) => current.filter((id) => response.data.features.some((feature) => String(feature.id) === id)));
    } catch (error) {
      setMessage(JSON.stringify(error.response?.data || { detail: 'Could not load features.' }));
    }
  }, [selectedSlug, appliedFilters]);

  useEffect(() => { if (access === 'allowed') loadFeatures(); }, [access, loadFeatures]);

  useEffect(() => {
    setSelectedIds([]); setDraftGeometry(null); setForm({}); setFilters([{ ...DEFAULT_FILTER }]);
    setAppliedFilters([]); setDistinctValues({}); setSymbologyField(''); setBatchField('');
    clearDraw(drawRef);
  }, [selectedSlug]);

  const selectWithinPolygon = useCallback((geometry) => {
    const ring = geometry?.coordinates?.[0] || [];
    const ids = featuresRef.current.features.filter((feature) => {
      const center = geometryCenter(feature.geometry);
      return center && pointInRing([center.longitude, center.latitude], ring);
    }).map((feature) => String(feature.id));
    setSelectedIds(ids);
    setDraftGeometry(null); setForm({});
    setMessage(`${ids.length} feature${ids.length === 1 ? '' : 's'} selected by area.`);
  }, []);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current.getMap();
    const draw = new MapboxDraw({ displayControlsDefault: false });
    drawRef.current = draw;
    map.addControl(draw, 'top-left');
    map.on('remove', () => {
      if (drawRef.current === draw) drawRef.current = null;
    });
    map.on('draw.create', (event) => {
      // The browser also emits a regular map click for the click that completes
      // a drawing. Without this guard, onClick immediately clears the result.
      ignoreNextMapClickRef.current = true;
      const geometry = event.features[0]?.geometry;
      if (drawPurposeRef.current === 'select') {
        selectWithinPolygon(geometry); clearDraw(drawRef); drawPurposeRef.current = 'create';
      } else {
        setDraftGeometry(geometry); setSelectedIds([]); setForm({});
      }
    });
    map.on('draw.update', (event) => setDraftGeometry(event.features[0]?.geometry || null));
    map.on('draw.delete', () => setDraftGeometry(null));
    loadFeatures();
  }, [loadFeatures, selectWithinPolygon]);

  const startDrawing = (geometryType) => {
    if (!dataset?.capabilities.create || !drawRef.current) return;
    const modes = { Point: 'draw_point', LineString: 'draw_line_string', Polygon: 'draw_polygon' };
    const accepted = dataset.geometry_type.replace('Multi', '');
    if (accepted !== geometryType) return setMessage(`${dataset.title} requires ${dataset.geometry_type} geometry.`);
    drawPurposeRef.current = 'create';
    runDrawAction(drawRef, (draw) => { draw.deleteAll(); draw.changeMode(modes[geometryType]); });
    setSelectedIds([]); setDraftGeometry(null); setForm({}); setMessage(`Draw a ${geometryType.toLowerCase()} on the map.`);
  };

  const startAreaSelection = () => {
    drawPurposeRef.current = 'select';
    runDrawAction(drawRef, (draw) => { draw.deleteAll(); draw.changeMode('draw_polygon'); });
    setMessage('Draw a polygon around the features you want to select.');
  };

  const selectFeature = (feature, additive = false) => {
    const id = String(feature.id);
    setSelectedIds((current) => additive
      ? (current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
      : [id]);
    setDraftGeometry(feature.geometry); setForm(feature.properties || {});
    clearDraw(drawRef);
    if (!additive && feature.geometry) {
      runDrawAction(drawRef, (draw) => {
        const drawIds = draw.add(feature) || [];
        if (drawIds[0]) draw.changeMode('direct_select', { featureId: drawIds[0] });
      });
    }
  };

  const clearSelection = () => {
    setSelectedIds([]); setDraftGeometry(null); setForm({}); clearDraw(drawRef);
  };

  const handleMapClick = (event) => {
    if (ignoreNextMapClickRef.current) {
      ignoreNextMapClickRef.current = false;
      return;
    }
    const drawMode = runDrawAction(drawRef, (draw) => draw.getMode?.() || '', '');
    if (drawMode.startsWith('draw_') || drawMode === 'direct_select') return;
    const hit = event.features?.[0];
    if (hit) {
      const feature = features.features.find((item) => String(item.id) === String(hit.id));
      if (feature) selectFeature(feature, event.originalEvent?.shiftKey);
    } else if (!event.originalEvent?.shiftKey) {
      clearSelection();
    }
  };

  const saveFeature = async () => {
    if (!draftGeometry) return setMessage('Draw or select a geometry first.');
    setBusy(true); setMessage('');
    const properties = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value === '' ? null : value]));
    try {
      if (selectedFeature) {
        await api.patch(apiRoutes.gisPlatform.feature(selectedSlug, selectedFeature.id), { geometry: draftGeometry, properties, version: selectedFeature.version });
      } else {
        await api.post(apiRoutes.gisPlatform.features(selectedSlug), { geometry: draftGeometry, properties, source: 'map' });
      }
      setMessage('Feature change submitted for review.'); clearSelection(); await loadFeatures();
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  const stageBatchUpdate = async () => {
    if (!selectedIds.length || !batchField) return;
    setBusy(true); setMessage('');
    try {
      await api.post(apiRoutes.gisPlatform.featureBatch(selectedSlug), {
        operation: 'update', ids: selectedIds, properties: { [batchField]: batchValue === '' ? null : batchValue },
      });
      setMessage(`Batch update for ${selectedIds.length} features submitted for review.`); clearSelection();
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (!selectedIds.length || !dataset?.capabilities.delete || !window.confirm(`Submit ${selectedIds.length} selected feature(s) for deletion review?`)) return;
    setBusy(true); setMessage('');
    try {
      if (selectedIds.length === 1) await api.delete(apiRoutes.gisPlatform.feature(selectedSlug, selectedIds[0]));
      else await api.post(apiRoutes.gisPlatform.featureBatch(selectedSlug), { operation: 'delete', ids: selectedIds });
      setMessage('Deletion submitted for review.'); clearSelection();
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  const updateFilter = (index, patchValue) => setFilters((current) => current.map((filter, filterIndex) => filterIndex === index ? { ...filter, ...patchValue } : filter));

  const loadDistinctValues = async (index, field) => {
    updateFilter(index, { field, operator: 'eq', value: '' });
    if (!field) return;
    try {
      const response = await api.get(apiRoutes.gisPlatform.featureValues(selectedSlug), { params: { field, limit: 200 } });
      setDistinctValues((current) => ({ ...current, [index]: response.data.values }));
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
  };

  const applyFilters = () => {
    const active = filters.filter((filter) => filter.field && (filter.operator === 'isnull' || filter.value !== ''));
    setAppliedFilters(active); setMessage(active.length ? `${active.length} filter condition(s) applied.` : 'Filters cleared.');
  };

  const loadImports = async () => {
    const response = await api.get(apiRoutes.gisPlatform.imports);
    const jobs = response.data.results || response.data;
    setImports(jobs);
    setImportMappings((current) => {
      const next = { ...current };
      jobs.forEach((job) => { if (!next[job.id]) next[job.id] = Object.keys(job.field_mapping || {}).length ? job.field_mapping : (job.inspection?.suggested_field_mapping || {}); });
      return next;
    });
  };

  const updateImportMapping = (jobId, destination, source) => setImportMappings((current) => {
    const mapping = { ...(current[jobId] || {}) };
    if (source) mapping[destination] = source; else delete mapping[destination];
    return { ...current, [jobId]: mapping };
  });

  const commitImport = async (job) => {
    setBusy(true); setMessage('');
    try {
      await api.post(apiRoutes.gisPlatform.importCommit(job.id), { source_crs: job.source_crs, field_mapping: importMappings[job.id] || {} });
      setMessage('Import is being staged for review.'); await loadImports();
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (tab === 'imports' && access === 'allowed') loadImports(); }, [tab, access]);

  const loadChanges = useCallback(async () => {
    try {
      const [changeResponse, agentResponse] = await Promise.all([
        api.get(apiRoutes.gisPlatform.changeSets),
        api.get(apiRoutes.gisPlatform.agentSubmissions),
      ]);
      setChanges(changeResponse.data.results || changeResponse.data);
      setAgentSubmissions(agentResponse.data.results || agentResponse.data);
    }
    catch (error) { setMessage(error.response?.data?.detail || 'Could not load review queue.'); }
  }, []);

  useEffect(() => { if (tab === 'audit' && access === 'allowed') loadChanges(); }, [tab, access, loadChanges]);

  const changeSetAction = async (change, action) => {
    const routes = { approve: apiRoutes.gisPlatform.changeSetApprove, reject: apiRoutes.gisPlatform.changeSetReject, publish: apiRoutes.gisPlatform.changeSetPublish };
    const notes = action === 'publish' ? '' : (window.prompt(`${action} notes`) || '');
    setBusy(true); setMessage('');
    try {
      const response = await api.post(routes[action](change.id), { notes });
      setMessage(action === 'publish' ? (response.status === 202 ? 'Publication queued.' : 'Published to GISData.') : `Change set ${action}d.`);
      await loadChanges(); if (action === 'publish' && response.status !== 202) await loadFeatures();
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  const agentSubmissionAction = async (submission, action) => {
    const route = action === 'approve' ? apiRoutes.gisPlatform.agentSubmissionApprove : apiRoutes.gisPlatform.agentSubmissionReject;
    const notes = window.prompt(`${action} notes`) || '';
    setBusy(true); setMessage('');
    try {
      await api.post(route(submission.id), { notes });
      setMessage(`Agent submission ${action}d.`); await loadChanges();
    } catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  const locateAgentSubmission = (submission) => {
    const center = geometryCenter(submission.geometry);
    if (!center || !mapRef.current) return setMessage('This submission does not include a geometry snapshot.');
    mapRef.current.getMap().flyTo({ center: [center.longitude, center.latitude], zoom: 16, essential: true });
  };

  const uploadImport = async (event) => {
    event.preventDefault(); const data = new FormData(event.currentTarget); data.set('dataset_slug', selectedSlug); setBusy(true);
    try { await api.post(apiRoutes.gisPlatform.imports, data, { headers: { 'Content-Type': 'multipart/form-data' } }); setMessage('Upload accepted. Inspection is running.'); event.currentTarget.reset(); await loadImports(); }
    catch (error) { setMessage(JSON.stringify(error.response?.data || { detail: error.message })); }
    finally { setBusy(false); }
  };

  if (access === 'loading') return <div className="gis-gate">Loading GIS Platform…</div>;
  if (access !== 'allowed') return <div className="gis-gate"><h1>GIS Platform</h1><p>{access === 'login' ? 'Please sign in.' : 'GIS Team membership is required.'}</p></div>;

  const popupEditor = selectedFeature && popupPosition && <Popup longitude={popupPosition.longitude} latitude={popupPosition.latitude} anchor="bottom" closeOnClick={false} onClose={clearSelection} maxWidth="360px">
    <div className="gis-popup-editor">
      <div className="gis-popup-heading"><strong>Edit #{selectedFeature.id}</strong><span>{dataset?.title}</span></div>
      <div className="gis-popup-fields">{dataset?.field_schema.map((field) => <label key={field.name}><span>{field.name}</span><FieldInput field={field} value={form[field.name]} onChange={(value) => setForm((current) => ({ ...current, [field.name]: value }))} /></label>)}</div>
      <div className="gis-popup-actions"><button disabled={busy || !dataset?.capabilities.change} onClick={saveFeature}>Submit edit</button><button className="danger" disabled={busy || !dataset?.capabilities.delete} onClick={deleteSelected}>Delete</button></div>
    </div>
  </Popup>;

  return <div className="gis-page">
    <Header />
    <div className={`gis-shell ${sidebarOpen ? '' : 'sidebar-closed'}`}>
      <aside className="gis-sidebar" aria-hidden={!sidebarOpen}>
        <div className="gis-title-row"><div className="gis-title"><span>GeoSpatial workspace</span><strong>Explore & edit</strong></div><button className="gis-icon-button" onClick={() => setSidebarOpen(false)} title="Collapse sidebar">←</button></div>
        <div className="gis-tabs">
          <button className={tab === 'layers' ? 'active' : ''} onClick={() => setTab('layers')}>Layers</button>
          <button className={tab === 'imports' ? 'active' : ''} onClick={() => setTab('imports')}>Imports</button>
          <button className={tab === 'audit' ? 'active' : ''} onClick={() => setTab('audit')}>Review</button>
        </div>

        {tab === 'layers' ? <div className="gis-panel-stack">
          <section className="gis-card gis-layer-card">
            <div className="gis-section-title"><span>Active layer</span><b>{features.features.length} visible</b></div>
            <select value={selectedSlug} onChange={(event) => setSelectedSlug(event.target.value)}>{datasets.filter((item) => item.map_enabled).map((item) => <option key={item.slug} value={item.slug}>{item.title}</option>)}</select>
            <div className="gis-meta"><span>{dataset?.geometry_type}</span><span>EPSG:{dataset?.srid}</span>{appliedFilters.length > 0 && <span>{appliedFilters.length} filters</span>}</div>
          </section>

          <details className="gis-card" open>
            <summary><span>Query builder</span><b>{appliedFilters.length || 'Filter'}</b></summary>
            <div className="gis-details-body">
              {filters.map((filter, index) => <div className="gis-filter-row" key={`filter-${index}`}>
                <select value={filter.field} onChange={(event) => loadDistinctValues(index, event.target.value)}><option value="">Column…</option>{dataset?.field_schema.map((field) => <option key={field.name} value={field.name}>{field.name}</option>)}</select>
                <select value={filter.operator} onChange={(event) => updateFilter(index, { operator: event.target.value, value: event.target.value === 'isnull' ? 'true' : '' })}>{operatorsForField(dataset?.field_schema.find((field) => field.name === filter.field)).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
                {filter.operator === 'isnull'
                  ? <select value={filter.value} onChange={(event) => updateFilter(index, { value: event.target.value })}><option value="true">Empty</option><option value="false">Not empty</option></select>
                  : <><input list={`gis-values-${index}`} placeholder="Value" value={filter.value} onChange={(event) => updateFilter(index, { value: event.target.value })} /><datalist id={`gis-values-${index}`}>{(distinctValues[index] || []).map((value) => <option value={fieldValue(value)} key={fieldValue(value)} />)}</datalist></>}
                <button className="gis-mini danger" onClick={() => setFilters((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
              </div>)}
              <div className="gis-inline-actions"><button onClick={() => setFilters((current) => [...current, { ...DEFAULT_FILTER }])}>+ condition</button><button className="primary" onClick={applyFilters}>Apply</button><button onClick={() => { setFilters([{ ...DEFAULT_FILTER }]); setAppliedFilters([]); }}>Clear</button></div>
            </div>
          </details>

          <details className="gis-card" open>
            <summary><span>Map tools</span><b>{selectedIds.length ? `${selectedIds.length} selected` : 'Select / draw'}</b></summary>
            <div className="gis-details-body">
              <div className="gis-tool-grid">
                <button disabled={!dataset?.capabilities.create || dataset?.geometry_type.replace('Multi', '') !== 'Point'} onClick={() => startDrawing('Point')}>●<span>Add point</span></button>
                <button disabled={!dataset?.capabilities.create || dataset?.geometry_type.replace('Multi', '') !== 'LineString'} onClick={() => startDrawing('LineString')}>╱<span>Add line</span></button>
                <button disabled={!dataset?.capabilities.create || dataset?.geometry_type.replace('Multi', '') !== 'Polygon'} onClick={() => startDrawing('Polygon')}>⬡<span>Add polygon</span></button>
                <button onClick={startAreaSelection}>⌁<span>Lasso select</span></button>
                <button onClick={clearSelection}>○<span>Deselect</span></button>
                <button className="danger" disabled={!selectedIds.length || !dataset?.capabilities.delete} onClick={deleteSelected}>⌫<span>Delete</span></button>
              </div>
              <p className="gis-hint">Shift-click features to build a selection. Drag vertices on a single selected feature to edit its geometry.</p>
            </div>
          </details>

          {draftGeometry && !selectedFeature && <details className="gis-card" open>
            <summary><span>New feature</span><b>{dataset?.geometry_type}</b></summary>
            <div className="gis-details-body gis-compact-form">{dataset?.field_schema.map((field) => <label key={field.name}><span>{field.name}{field.required ? ' *' : ''}</span><FieldInput field={field} value={form[field.name]} onChange={(value) => setForm((current) => ({ ...current, [field.name]: value }))} /></label>)}<button className="gis-primary" disabled={busy} onClick={saveFeature}>Submit for review</button></div>
          </details>}

          {selectedIds.length > 1 && <details className="gis-card" open>
            <summary><span>Batch edit</span><b>{selectedIds.length} features</b></summary>
            <div className="gis-details-body gis-batch-editor"><select value={batchField} onChange={(event) => { setBatchField(event.target.value); setBatchValue(''); }}><option value="">Field to update…</option>{dataset?.field_schema.map((field) => <option value={field.name} key={field.name}>{field.name}</option>)}</select>{batchField && <FieldInput field={dataset?.field_schema.find((field) => field.name === batchField)} value={batchValue} onChange={setBatchValue} />}<button className="gis-primary" disabled={busy || !batchField || !dataset?.capabilities.change} onClick={stageBatchUpdate}>Stage batch update</button></div>
          </details>}

          <details className="gis-card">
            <summary><span>Symbology</span><b>{symbologyField || 'Single color'}</b></summary>
            <div className="gis-details-body gis-style-controls">
              <label><span>Classify by</span><select value={symbologyField} onChange={(event) => setSymbologyField(event.target.value)}><option value="">No classification</option>{dataset?.field_schema.map((field) => <option value={field.name} key={field.name}>{field.name}</option>)}</select></label>
              <div className="gis-control-row"><label><span>Method</span><select value={classification} onChange={(event) => setClassification(event.target.value)}><option value="equal">Equal interval</option><option value="manual">Manual breaks</option></select></label><label><span>Palette</span><select value={palette} onChange={(event) => setPalette(event.target.value)}>{Object.keys(PALETTES).map((name) => <option key={name}>{name}</option>)}</select></label></div>
              {classification === 'equal' ? <label><span>Classes</span><input type="number" min="2" max="5" value={classCount} onChange={(event) => setClassCount(event.target.value)} /></label> : <label><span>Breaks, comma separated</span><input value={manualBreaks} onChange={(event) => setManualBreaks(event.target.value)} placeholder="10, 25, 50, 100" /></label>}
              <div className="gis-legend">{symbology.legend.map((item) => <div key={item.label}><i style={{ background: item.color }} /><span>{item.label}</span></div>)}</div>
            </div>
          </details>

          <details className="gis-card">
            <summary><span>Visible features</span><b>{features.features.length}</b></summary>
            <div className="gis-feature-list">{features.features.map((feature) => <button key={feature.id} className={selectedIds.includes(String(feature.id)) ? 'active' : ''} onClick={(event) => selectFeature(feature, event.shiftKey)}><strong>#{feature.id}</strong><span>{feature.properties.name || feature.properties.parcel_id || feature.properties.type || dataset?.title}</span></button>)}</div>
          </details>
        </div> : tab === 'imports' ? <div className="gis-panel-stack">
          <form className="gis-card gis-import-form" onSubmit={uploadImport}><h3>Import into {dataset?.title}</h3><input name="source_file" type="file" accept=".geojson,.json,.zip,.gpkg" required /><input name="source_crs" placeholder="Source CRS, e.g. EPSG:4326" /><button className="gis-primary" disabled={busy || !dataset?.capabilities.import}>Upload and inspect</button></form>
          <button className="gis-secondary" onClick={loadImports}>Refresh jobs</button>
          <div className="gis-list">{imports.map((job) => { const target = datasets.find((item) => item.slug === job.dataset); return <div className="gis-job" key={job.id}><strong>{job.source_filename}</strong><span>{job.dataset} · {job.status}</span><small>{job.inspection?.feature_count ?? 0} features</small>{job.errors?.map((error) => <small className="gis-error" key={error}>{error}</small>)}{job.status === 'ready' && <div className="gis-mapping"><b>Field mapping</b>{target?.editable_fields.map((destination) => <label key={destination}><span>{destination}</span><select value={importMappings[job.id]?.[destination] || ''} onChange={(event) => updateImportMapping(job.id, destination, event.target.value)}><option value="">Do not import</option>{job.inspection?.fields?.map((source) => <option key={source} value={source}>{source}</option>)}</select></label>)}<button disabled={busy} onClick={() => commitImport(job)}>Stage for review</button></div>}</div>; })}</div>
        </div> : <div className="gis-panel-stack">
          <div className="gis-section-title"><h3>Review queue</h3><button className="gis-mini" onClick={loadChanges}>Refresh</button></div>
          <details className="gis-card" open>
            <summary><span>Agent field submissions</span><b>{agentSubmissions.filter((item) => item.status === 'pending_review').length} pending</b></summary>
            <div className="gis-details-body gis-review-list">{agentSubmissions.map((submission) => <div className="gis-job gis-agent-job" key={submission.id}>
              <strong>{submission.title}</strong>
              <span>{submission.category_label} · {submission.status}</span>
              <small>{submission.agent_name} · {new Date(submission.created_at).toLocaleString()}</small>
              <div className="gis-submission-properties">{Object.entries(submission.properties || {}).slice(0, 8).map(([key, value]) => <span key={key}><b>{key}</b>{value === null || value === '' ? '—' : String(value)}</span>)}</div>
              {submission.review_notes && <small>Review note: {submission.review_notes}</small>}
              <div className="gis-review-actions"><button className="locate" onClick={() => locateAgentSubmission(submission)}>Locate</button>{dataset?.capabilities.review && submission.status === 'pending_review' && String(submission.agent_id) !== String(currentUser?.id) && <><button disabled={busy} onClick={() => agentSubmissionAction(submission, 'approve')}>Approve</button><button className="reject" disabled={busy} onClick={() => agentSubmissionAction(submission, 'reject')}>Reject</button></>}</div>
            </div>)}</div>
          </details>
          <div className="gis-section-title"><h3>GIS change sets</h3></div>
          <div className="gis-list">{changes.map((change) => <div className="gis-job" key={change.id}><strong>{change.title}</strong><span>{change.dataset_slug} · {change.source} · {change.status}</span><small>{change.created_by_name} · {new Date(change.created_at).toLocaleString()}</small><small>{change.change_count} operation{change.change_count === 1 ? '' : 's'}</small>{change.reviewed_by_name && <small>Reviewed by {change.reviewed_by_name}</small>}{change.status === 'pending_review' && String(change.created_by_id) !== String(currentUser?.id) && <div className="gis-review-actions"><button disabled={busy} onClick={() => changeSetAction(change, 'approve')}>Approve</button><button className="reject" disabled={busy} onClick={() => changeSetAction(change, 'reject')}>Reject</button></div>}{change.status === 'approved' && change.reviewed_by_name === currentUser?.username && <button disabled={busy} onClick={() => changeSetAction(change, 'publish')}>Publish to GISData</button>}</div>)}</div>
        </div>}
        {message && <div className="gis-message"><button onClick={() => setMessage('')}>×</button>{message}</div>}
      </aside>

      <main className="gis-map">
        {!sidebarOpen && <button className="gis-sidebar-toggle" onClick={() => setSidebarOpen(true)}>Layers →</button>}
        <div className="gis-basemap-control"><span>Basemap</span><select value={basemap} onChange={(event) => setBasemap(event.target.value)}>{Object.entries(BASEMAPS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}</select></div>
        <div className="gis-selection-badge">{selectedIds.length ? `${selectedIds.length} selected` : 'Shift-click for multi-select'}</div>
        <Map ref={mapRef} initialViewState={{ longitude: 51.389, latitude: 35.6892, zoom: 11 }} mapStyle={mapStyle} onLoad={handleMapLoad} onMoveEnd={loadFeatures} onClick={handleMapClick} interactiveLayerIds={['gis-fill', 'gis-line', 'gis-point']}>
          <NavigationControl position="bottom-right" /><FullscreenControl position="bottom-right" /><GeolocateControl position="bottom-right" /><ScaleControl />
          <Source id="gis-data" type="geojson" data={features}>
            <Layer id="gis-fill" type="fill" filter={['==', '$type', 'Polygon']} paint={{ 'fill-color': symbology.color, 'fill-opacity': 0.52, 'fill-outline-color': '#163b2d' }} />
            <Layer id="gis-line" type="line" filter={['==', '$type', 'LineString']} paint={{ 'line-color': symbology.color, 'line-width': 5 }} />
            <Layer id="gis-point" type="circle" filter={['==', '$type', 'Point']} paint={{ 'circle-color': symbology.color, 'circle-radius': 7, 'circle-stroke-width': 2.5, 'circle-stroke-color': '#fff' }} />
          </Source>
          <Source id="gis-selected" type="geojson" data={selectedCollection}>
            <Layer id="gis-selected-fill" type="fill" filter={['==', '$type', 'Polygon']} paint={{ 'fill-color': '#facc15', 'fill-opacity': 0.62, 'fill-outline-color': '#b91c1c' }} />
            <Layer id="gis-selected-outline" type="line" filter={['==', '$type', 'Polygon']} paint={{ 'line-color': '#dc2626', 'line-width': 5 }} />
            <Layer id="gis-selected-line" type="line" filter={['==', '$type', 'LineString']} paint={{ 'line-color': '#dc2626', 'line-width': 8 }} />
            <Layer id="gis-selected-point" type="circle" filter={['==', '$type', 'Point']} paint={{ 'circle-color': '#facc15', 'circle-radius': 10, 'circle-stroke-width': 4, 'circle-stroke-color': '#dc2626' }} />
          </Source>
          {tab === 'audit' && <Source id="gis-agent-submissions" type="geojson" data={agentSubmissionCollection}>
            <Layer id="gis-agent-fill" type="fill" filter={['==', '$type', 'Polygon']} paint={{ 'fill-color': ['match', ['get', 'status'], 'approved', '#16a34a', 'rejected', '#dc2626', '#7c3aed'], 'fill-opacity': 0.35, 'fill-outline-color': '#4c1d95' }} />
            <Layer id="gis-agent-outline" type="line" filter={['==', '$type', 'Polygon']} paint={{ 'line-color': '#7c3aed', 'line-width': 5 }} />
            <Layer id="gis-agent-line" type="line" filter={['==', '$type', 'LineString']} paint={{ 'line-color': '#7c3aed', 'line-width': 7 }} />
            <Layer id="gis-agent-point" type="circle" filter={['==', '$type', 'Point']} paint={{ 'circle-color': ['match', ['get', 'status'], 'approved', '#16a34a', 'rejected', '#dc2626', '#f59e0b'], 'circle-radius': 10, 'circle-stroke-width': 4, 'circle-stroke-color': '#fff' }} />
          </Source>}
          {popupEditor}
        </Map>
      </main>
    </div>
  </div>;
}
